import { getBackendLanguage } from '@assets/i18n/config';
import type {
  AccountSearchInput,
  SearchAccountsForReconFlagsGridQueryVariables,
} from '@generated/graphql';
import {
  type LanguageCodes as GqlLanguageCodes,
  SearchAccountStates,
} from '@generated/graphql';
import {
  RECON_FLAGS_ACCOUNT_SEARCH_QUERY_FIELDS,
  RECON_FLAGS_ACCOUNT_SEARCH_SORT_FIELD,
  RECON_FLAGS_MAX_ACCOUNT_IDS,
  RECON_FLAGS_MIN_SEARCH_LENGTH,
} from './reconFlags.config';

const SEARCH_ACCOUNT_STATES: SearchAccountStates[] = [
  SearchAccountStates.Active,
  SearchAccountStates.Requested,
  SearchAccountStates.Frozen,
  SearchAccountStates.Failed,
  SearchAccountStates.Initiated,
  SearchAccountStates.Ready,
  SearchAccountStates.Canceled,
  SearchAccountStates.Inactive,
];

/** Variables for `searchAccountsForReconFlagsGrid`. */
export function buildSearchAccountsForReconFlagsVariables(
  orgId: string,
  query: string,
): SearchAccountsForReconFlagsGridQueryVariables {
  const input: AccountSearchInput = {
    filter: {
      query,
      queryFields: [...RECON_FLAGS_ACCOUNT_SEARCH_QUERY_FIELDS],
      organizationId: orgId,
      restrictToOrganizationId: false,
      // ov-enums and GraphQL LanguageCodes share values (`en` / `fr` / `es`).
      language: getBackendLanguage() as unknown as GqlLanguageCodes,
      states: SEARCH_ACCOUNT_STATES,
    },
    pagination: {
      perPage: RECON_FLAGS_MAX_ACCOUNT_IDS,
      sortField: RECON_FLAGS_ACCOUNT_SEARCH_SORT_FIELD,
    },
  };
  return { input };
}

type AccountSearchHit = { id: string };

/**
 * Derives `accountIds` for `fetchFlagsForGrid`:
 * - `undefined` → omit field (search inactive or still resolving)
 * - `[]` → search active, no matches (empty grid)
 * - non-empty → filter flags to those accounts
 */
export function buildReconFlagsAccountIdsFilter(
  debouncedSearch: string,
  options: {
    loading: boolean;
    accounts: readonly AccountSearchHit[] | undefined;
    /** Trimmed query that produced `accounts`; must match `debouncedSearch` to apply IDs. */
    accountsForSearchQuery?: string | null;
  },
): string[] | undefined {
  const trimmed = debouncedSearch.trim();
  if (trimmed.length < RECON_FLAGS_MIN_SEARCH_LENGTH) {
    return undefined;
  }
  if (options.accountsForSearchQuery !== trimmed) {
    return undefined;
  }
  if (options.loading) {
    return undefined;
  }
  const ids = (options.accounts ?? [])
    .map((account) => account.id)
    .slice(0, RECON_FLAGS_MAX_ACCOUNT_IDS);
  return ids;
}

/** True when the flags grid query should wait for account search to finish. */
export function shouldSkipFetchFlagsForAccountSearch(
  debouncedSearch: string,
  accountSearchLoading: boolean,
  accountsForSearchQuery?: string | null,
): boolean {
  const trimmed = debouncedSearch.trim();
  if (trimmed.length < RECON_FLAGS_MIN_SEARCH_LENGTH) {
    return false;
  }
  if (accountSearchLoading) {
    return true;
  }
  return accountsForSearchQuery !== trimmed;
}
